import React from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import {
  LayoutDashboard,
  Users,
  Wrench,
  QrCode,
  Activity,
  HeartPulse,
  FileSpreadsheet,
  PlugZap,
  Palette,
  LogOut,
  Smartphone,
  Settings,
  AlertTriangle,
} from "lucide-react";
import BrandMark from "@/components/app/BrandMark";

const nav = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/workers", label: "Employees", icon: Users, testid: "nav-workers" },
  { to: "/tools", label: "Tools", icon: Wrench, testid: "nav-tools" },
  { to: "/qr-labels", label: "QR labels", icon: QrCode, testid: "nav-qr" },
  { to: "/exposures", label: "Exposure logs", icon: Activity, testid: "nav-exposures" },
  { to: "/checkins", label: "Check-ins", icon: HeartPulse, testid: "nav-checkins" },
  { to: "/reports", label: "Reports / PDF", icon: FileSpreadsheet, testid: "nav-reports" },
  { to: "/safetyculture", label: "SafetyCulture", icon: PlugZap, testid: "nav-safetyculture" },
  { to: "/branding", label: "Branding", icon: Palette, testid: "nav-branding" },
  { to: "/account", label: "Account", icon: Settings, testid: "nav-account" },
];

export default function ManagerLayout() {
  const { user, logout, accountStatus } = useAuth();
  const navigate = useNavigate();

  const doLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  const isTrial =
    accountStatus?.company?.subscription_status === "trial" && accountStatus?.active;
  const daysLeft = accountStatus?.trial_days_remaining ?? 0;

  return (
    <div className="min-h-screen flex bg-slate-50">
      {/* Sidebar */}
      <aside className="hidden lg:flex w-64 flex-col bg-white border-r border-slate-200">
        <div className="h-16 flex items-center px-6 border-b border-slate-200">
          <BrandMark size="md" tone="dark" />
        </div>
        <nav className="flex-1 px-3 py-6 space-y-1">
          {nav.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={n.testid}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                  isActive
                    ? "bg-slate-900 text-white"
                    : "text-slate-700 hover:bg-slate-100"
                }`
              }
            >
              <n.icon className="h-4 w-4" />
              {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="px-3 py-4 border-t border-slate-200 space-y-1">
          <NavLink
            to="/worker"
            data-testid="nav-worker-app"
            className="flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium text-slate-700 hover:bg-slate-100"
          >
            <Smartphone className="h-4 w-4" />
            Open employee app
          </NavLink>
          <div className="px-3 py-2 text-xs text-slate-500">
            <p className="font-bold uppercase tracking-wider mb-1">Signed in</p>
            <p className="text-slate-700 font-medium truncate">{user?.name}</p>
            <p className="truncate">{user?.email}</p>
          </div>
          <button
            onClick={doLogout}
            data-testid="manager-logout-btn"
            className="w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium text-slate-700 hover:bg-slate-100"
          >
            <LogOut className="h-4 w-4" />
            Sign out
          </button>
        </div>
      </aside>

      {/* Mobile topbar */}
      <div className="lg:hidden fixed top-0 left-0 right-0 h-14 bg-white border-b border-slate-200 flex items-center justify-between px-4 z-30">
        <BrandMark size="sm" tone="dark" />
        <button
          onClick={doLogout}
          data-testid="manager-mobile-logout-btn"
          className="text-xs font-medium text-slate-700"
        >
          <LogOut className="h-4 w-4" />
        </button>
      </div>

      {/* Mobile bottom nav */}
      <nav className="lg:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 grid grid-cols-4 z-30">
        {nav.slice(0, 4).map((n) => (
          <NavLink
            key={n.to}
            to={n.to}
            className={({ isActive }) =>
              `flex flex-col items-center justify-center py-2 text-[10px] ${
                isActive ? "text-slate-900" : "text-slate-500"
              }`
            }
          >
            <n.icon className="h-5 w-5 mb-0.5" />
            {n.label.split(" ")[0]}
          </NavLink>
        ))}
      </nav>

      {/* Main */}
      <main className="flex-1 lg:pt-0 pt-14 lg:pb-0 pb-16 overflow-x-hidden">
        <div className="p-4 sm:p-6 md:p-8 lg:p-10 max-w-[1600px] mx-auto">
          {isTrial && (
            <div
              className="mb-6 flex flex-wrap items-center gap-3 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900"
              data-testid="trial-banner"
            >
              <AlertTriangle className="h-4 w-4 flex-shrink-0" />
              <span>
                Free trial — <strong>{daysLeft}</strong> day{daysLeft === 1 ? "" : "s"} remaining
                {accountStatus?.company?.name && ` for ${accountStatus.company.name}`}.
              </span>
              <NavLink
                to="/account"
                className="ml-auto font-bold underline"
                data-testid="trial-banner-upgrade"
              >
                Upgrade →
              </NavLink>
            </div>
          )}
          <Outlet />
        </div>
      </main>
    </div>
  );
}
